import { useAuth, authorizedFetch } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface ConditionReport {
  evaluation_id: string
  analysis_id?: string | null
  analysis_status?: string | null
  final_score: number
  created_at: string
}

export function useConditionReport(conditionId: string | null | undefined) {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const latest = useQuery({
    queryKey: ['condition-report', conditionId],
    queryFn: async (): Promise<ConditionReport | null> => {
      const response = await authorizedFetch(
        `/api/evaluations/conditions/${encodeURIComponent(conditionId!)}/report`,
      )
      if (response.status === 404) return null
      if (!response.ok) throw new Error('Failed to load condition report')
      return response.json()
    },
    enabled: !!token && !!conditionId,
    staleTime: 30 * 1000,
  })

  const generate = useMutation({
    mutationFn: async (): Promise<ConditionReport> => {
      const response = await authorizedFetch(
        `/api/evaluations/conditions/${encodeURIComponent(conditionId!)}/report`,
        { method: 'POST' },
      )
      if (!response.ok) {
        const body = await response.json().catch(() => null)
        throw new Error(body?.detail || 'Failed to generate condition report')
      }
      return response.json()
    },
    onSuccess: (report) => {
      queryClient.setQueryData(['condition-report', conditionId], report)
      queryClient.invalidateQueries({ queryKey: ['evaluation-analysis', report.evaluation_id] })
    },
  })

  return {
    report: latest.data ?? null,
    isLoading: latest.isLoading,
    error: latest.error,
    generateReport: generate.mutate,
    isGenerating: generate.isPending,
    generateError: generate.error,
  }
}
